import type { VRM } from '@pixiv/three-vrm'
import { type MutableRefObject, useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import type { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { loadVrma } from '../lib/loadVrma'
import type { MotionItem, MotionSequence } from '../types'
import type { InitialMotionHandlers } from './useInitialMotionPreparation'

type UseVrmaMotionParams = {
  vrm: VRM | undefined
  mixerRef: MutableRefObject<THREE.AnimationMixer | null>
  gltfLoader: GLTFLoader
  motionSequence: MotionSequence
  initialMotionHandlers: InitialMotionHandlers
}

type UseVrmaMotionResult = {
  activeIndex: number
  activeMotion: MotionItem | undefined
  activeMotionReady: boolean
}

type PlaybackState = {
  restartKey: string | number | undefined
  activeIndex: number
}

const getNextMotionIndex = (current: number, motionCount: number): number => {
  const next = current + 1
  return next < motionCount ? next : current
}

const getPlaybackKey = (motion: MotionItem): string => {
  if (motion.playback.type === 'duration') return `duration:${motion.playback.durationMs}`
  if (motion.playback.type === 'repeat') return `repeat:${motion.playback.count}`
  return 'loop'
}

const getMotionInstanceKey = ({
  restartKey,
  activeIndex,
  activeMotion,
}: {
  restartKey: MotionSequence['restartKey']
  activeIndex: number
  activeMotion: MotionItem | undefined
}): string => {
  if (!activeMotion) return `${String(restartKey)}:${activeIndex}:empty`
  return `${String(restartKey)}:${activeIndex}:${activeMotion.source}:${getPlaybackKey(activeMotion)}`
}

/** MotionItem の再生設定を three.js の AnimationAction に反映する。 */
const configureActionPlayback = (action: THREE.AnimationAction, motion: MotionItem): void => {
  if (motion.playback.type === 'loop') {
    action.setLoop(THREE.LoopRepeat, Infinity)
    action.clampWhenFinished = false
    return
  }

  if (motion.playback.type === 'repeat') {
    action.setLoop(THREE.LoopRepeat, motion.playback.count)
    action.clampWhenFinished = true
    return
  }

  action.setLoop(THREE.LoopOnce, 1)
  action.clampWhenFinished = true
}

/**
 * VRM と motionSequence を受け取り、現在の VRMA を読み込んで AnimationMixer へ適用する。
 *
 * この hook は VRMModel から再生制御を切り離すための処理で、主に以下を担当する。
 *
 * - motionSequence の先頭から順に activeMotion を選ぶ
 * - activeMotion.source の VRMA を loadVrma で AnimationClip に変換する
 * - AnimationMixer へ AnimationAction として登録して再生する
 * - 前の motion から次の motion へ transition.durationMs でクロスフェードする
 * - 再生終了イベントを受けて次の motion へ進める
 *
 * VRMA のロード自体は loadVrma に委譲し、
 * この hook では React state と three.js の再生状態だけを管理する。
 */
export const useVrmaMotion = ({
  vrm,
  mixerRef,
  gltfLoader,
  motionSequence,
  initialMotionHandlers,
}: UseVrmaMotionParams): UseVrmaMotionResult => {
  const { restartKey } = motionSequence
  const [playbackState, setPlaybackState] = useState<PlaybackState>(() => ({
    restartKey,
    activeIndex: 0,
  }))
  const actionRef = useRef<{ action: THREE.AnimationAction; mixer: THREE.AnimationMixer } | null>(null)
  const activeIndex = playbackState.restartKey === restartKey ? playbackState.activeIndex : 0
  const activeMotion = motionSequence.items[activeIndex]
  const [loadedMotionKey, setLoadedMotionKey] = useState<string | null>(null)
  const activeMotionKey = getMotionInstanceKey({ restartKey, activeIndex, activeMotion })
  const activeMotionReady = loadedMotionKey === activeMotionKey

  // Storybook の controls は motionSequence オブジェクトを作り直すため、参照差分だけではリスタートしない。
  // source / playback / restartKey が変わった時だけ「別 sequence」として先頭へ戻す。
  useEffect(() => {
    setPlaybackState((current) => (current.restartKey === restartKey ? current : { restartKey, activeIndex: 0 }))
  }, [restartKey])

  useEffect(() => {
    const mixer = mixerRef.current
    if (!vrm || !mixer) return

    if (!activeMotion) {
      initialMotionHandlers.revealWithoutMotion(vrm.scene)
      setLoadedMotionKey(activeMotionKey)
      return
    }

    let canceled = false
    // three.js の finished イベントは action 単位ではなく mixer 単位で届くため、
    // effect の cleanup で同じ listener を確実に外せるように先に定義しておく。
    const handleFinished = () => {
      setPlaybackState((current) => {
        if (current.restartKey !== restartKey) return current
        return {
          restartKey,
          activeIndex: getNextMotionIndex(current.activeIndex, motionSequence.items.length),
        }
      })
    }

    const playMotion = async () => {
      const isCurrent = () => !canceled && mixerRef.current === mixer
      const result = await loadVrma({ source: activeMotion.source, vrm, gltfLoader, isCurrent })
      if (!isCurrent()) return

      if (result.status !== 'loaded') {
        initialMotionHandlers.revealWithoutMotion(vrm.scene)
        setLoadedMotionKey(activeMotionKey)
        return
      }

      const previousAction = actionRef.current?.mixer === mixer ? actionRef.current.action : null
      const action = mixer.clipAction(result.clip)
      actionRef.current = { action, mixer }

      action.reset()
      configureActionPlayback(action, activeMotion)
      action.play()

      if (!initialMotionHandlers.isInitialMotionPrepared()) {
        initialMotionHandlers.applyInitialPoseAndReveal(vrm, mixer)
      }

      if (previousAction && previousAction !== action) {
        // three.js の crossFadeFrom は秒指定なので、MotionTransition のミリ秒指定から変換する。
        action.crossFadeFrom(previousAction, (activeMotion.transition?.durationMs ?? 350) / 1000, false)
      }

      setLoadedMotionKey(activeMotionKey)
    }

    mixer.addEventListener('finished', handleFinished)
    void playMotion()

    return () => {
      canceled = true
      mixer.removeEventListener('finished', handleFinished)
    }
  }, [
    vrm,
    mixerRef,
    gltfLoader,
    activeMotion,
    activeMotionKey,
    restartKey,
    motionSequence.items.length,
    initialMotionHandlers,
  ])

  return {
    activeIndex,
    activeMotion,
    activeMotionReady,
  }
}
